import React, { useState } from "react";
import { Share2, Copy, ExternalLink, Check, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePlatformMessages } from "@/lib/usePlatformMessages";
import { Button } from "@/components/ui/button";

/**
 * ShareDiplomaButton
 *
 * Triggers the public diploma share flow:
 *   1. POST /api/diploma/share/{credentialId} → returns a short token
 *   2. Builds the public URL  {BACKEND}/api/share/{token}
 *   3. Shows a modal with copy + WhatsApp / Facebook / X share buttons
 *      and the OG image preview the social scrapers will see.
 *
 * Props:
 *   credentialId — required (competition only)
 *   variant      — "primary" (default) | "ghost"
 *   label        — optional button text override
 */
export default function ShareDiplomaButton({ credentialId, variant = "primary", label }) {
  const { m } = usePlatformMessages();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState(null); // { page_url, og_image_url, pdf_url, name, competition }
  const [copied, setCopied] = useState(false);

  if (!credentialId) return null;

  const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const handleOpen = async () => {
    if (share) {
      setOpen(true);
      return;
    }
    setLoading(true);
    try {
      const resp = await api.post(`/diploma/share/${credentialId}`);
      const token = resp?.data?.token;
      if (!token) throw new Error("missing token");
      const infoResp = await api.get(`/share/${token}/info`);
      const info = infoResp?.data || {};
      // Force HTTPS + public host even if backend used a different scheme.
      const safe = (url) => {
        if (!url || !baseUrl) return url;
        try {
          const u = new URL(url);
          const b = new URL(baseUrl);
          u.protocol = b.protocol;
          u.host = b.host;
          return u.toString();
        } catch {
          return url;
        }
      };
      setShare({
        token,
        page_url: safe(info.page_url) || `${baseUrl}/api/share/${token}`,
        og_image_url: safe(info.og_image_url) || `${baseUrl}/api/share/${token}/og.png`,
        pdf_url: safe(info.pdf_url) || `${baseUrl}/api/share/${token}/pdf`,
        name: info.name,
        competition: info.competition,
        time_formatted: info.time_formatted,
      });
      setOpen(true);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || m("share_generate_failed", null, "Não foi possível gerar o link de partilha. Termina o jogo primeiro.")
      );
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!share?.page_url) return;
    try {
      await navigator.clipboard.writeText(share.page_url);
      setCopied(true);
      toast.success(m("share_link_copied", null, "Link copiado!"));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(m("share_copy_failed", null, "Não foi possível copiar"));
    }
  };

  const tryNativeShare = async () => {
    if (!navigator.share || !share?.page_url) return;
    try {
      await navigator.share({
        title: `${share.name} conquistou o diploma SUPERTMATIK!`,
        text: `Vê o diploma de ${share.name} em ${share.competition}.`,
        url: share.page_url,
      });
    } catch {
      // user cancelled — no-op
    }
  };

  const text = share
    ? encodeURIComponent(
        `${share.name} conquistou o diploma SUPERTMATIK em ${share.competition}! ${share.page_url}`
      )
    : "";
  const url = share ? encodeURIComponent(share.page_url) : "";

  const buttonClass =
    variant === "ghost"
      ? "rounded-full text-sky-600 hover:bg-sky-50 font-bold"
      : "bg-amber-500 hover:bg-amber-600 text-white rounded-full font-bold";

  return (
    <>
      <Button
        type="button"
        onClick={handleOpen}
        disabled={loading}
        className={buttonClass}
        data-testid="btn-share-diploma"
      >
        <Share2 className="w-4 h-4 mr-2" />
        {loading ? "A gerar…" : label || "Partilhar diploma"}
      </Button>

      <AnimatePresence>
        {open && share && (
          <motion.div
            className="fixed inset-0 z-[200] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="share-modal-overlay"
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              initial={{ scale: 0.92, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 16 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="share-modal"
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                <h3 className="font-[Fredoka] font-bold text-slate-900">Partilhar diploma</h3>
                <button
                  className="text-slate-400 hover:text-slate-700"
                  onClick={() => setOpen(false)}
                  data-testid="share-modal-close"
                  aria-label="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                  <img
                    src={share.og_image_url}
                    alt={`Pré-visualização: ${share.name}`}
                    className="w-full block"
                    data-testid="share-modal-preview"
                  />
                </div>

                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <input
                    readOnly
                    value={share.page_url}
                    className="flex-1 bg-transparent text-xs text-slate-700 outline-none truncate"
                    onFocus={(e) => e.target.select()}
                    data-testid="share-modal-url"
                  />
                  <button
                    onClick={copyLink}
                    className="flex items-center gap-1 text-xs font-bold text-sky-600 hover:text-sky-700"
                    data-testid="share-modal-copy"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copiado" : "Copiar"}
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <a
                    href={`https://wa.me/?text=${text}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1 py-2 rounded-lg bg-[#25D366] text-white font-bold text-xs hover:opacity-90"
                    data-testid="share-modal-whatsapp"
                  >
                    <span className="text-base font-bold">WhatsApp</span>
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1 py-2 rounded-lg bg-[#1877F2] text-white font-bold text-xs hover:opacity-90"
                    data-testid="share-modal-facebook"
                  >
                    <span className="text-base font-bold">Facebook</span>
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${text}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1 py-2 rounded-lg bg-slate-900 text-white font-bold text-xs hover:opacity-90"
                    data-testid="share-modal-twitter"
                  >
                    <span className="text-base font-bold">X</span>
                  </a>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <a
                    href={share.page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold"
                    data-testid="share-modal-open"
                  >
                    <ExternalLink className="w-4 h-4" /> Abrir página
                  </a>
                  {typeof navigator !== "undefined" && navigator.share && (
                    <button
                      type="button"
                      onClick={tryNativeShare}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold"
                      data-testid="share-modal-native"
                    >
                      <Share2 className="w-4 h-4" /> Partilhar…
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
